// Utility to prevent special character input for numeric fields
// Allows only numbers and a single dot (for decimals), blocks all other characters


// Use onKeyDown to block invalid characters and preserve the current value
export function preventSpecialCharKeyDown(e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) {
  const allowedKeys = [
    'Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Home', 'End',
  ];
  // Allow: digits, one dot (if not already present), navigation keys
  if (
    allowedKeys.includes(e.key) ||
    (e.ctrlKey || e.metaKey) // Allow copy, paste, select all, etc.
  ) {
    return;
  }
  // Allow one dot if not present
  if (e.key === '.') {
    const value = (e.currentTarget as HTMLInputElement).value;
    if (value.includes('.')) {
      e.preventDefault();
    }
    return;
  }
  // Block anything that's not a digit
  if (!/\d/.test(e.key)) {
    e.preventDefault();
  }
}

// Optionally, you can add a version that allows only integers:
export function preventNonNumericInput(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) {
  const input = e.currentTarget;
  input.value = input.value.replace(/\D/g, "");
}

//prevent user to enter special characters in text fields
export const preventSpecialCharKeyDownInTextfield = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z0-9 ]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

export const preventSpecialCharKeyDownInTextfieldAllowPlus = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z0-9 +]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

export const preventSpecialCharKeyAndNumbersDownInTextfield = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // allow control keys
  if (allowedKeys.includes(e.key)) {
    return;
  }

  // allow only letters, numbers, and space
  const regex = /^[a-zA-Z ]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Prevents users from entering spaces in input fields (e.g. email).
 */
export const preventSpaceInput = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  if (e.key === " ") {
    e.preventDefault();
  }
};

/**
 * Removes all spaces from pasted input (useful for email fields).
 */
export const removeSpacesFromInput = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const input = e.currentTarget;
  const valueWithoutSpaces = input.value.replace(/\s+/g, "");

  if (input.value !== valueWithoutSpaces) {
    const cursor = input.selectionStart ?? valueWithoutSpaces.length;

    input.value = valueWithoutSpaces;

    requestAnimationFrame(() => {
      input.setSelectionRange(cursor, cursor);
    });
  }
};

/**
 * Prevents users from entering special characters.
 * Allows only letters, numbers, space, comma, period, @, +, ', and /
 */
export const preventInvalidTextInput = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // Allow navigation keys
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
    return;
  }

  const regex = /^[a-zA-Z0-9\s,.@+'/]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};

/**
 * Removes invalid characters from pasted or autofilled text.
 */
export const removeInvalidTextCharacters = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const input = e.currentTarget;

  const sanitizedValue = input.value.replace(
    /[^a-zA-Z0-9\s,.@+'/]/g,
    ""
  );

  if (input.value !== sanitizedValue) {
    input.value = sanitizedValue;
  }
};

/**
 * Every character that may legally appear in an email address this app accepts.
 * Mirrors EMAIL_REGEX in util/validation — keep the two in step.
 *
 * NOTE: the set is intentionally wider than the regex. A sanitiser runs on
 * every keystroke, when the address is still half-typed, so it may only strip
 * characters that can NEVER be valid anywhere in an address. Deciding whether
 * the finished string is well-formed is the regex's job, on submit.
 */
const INVALID_EMAIL_CHARS = /[^A-Za-z0-9!#$%&'*+/=?^_`{|}~.@-]/g;

/**
 * Strips characters that cannot appear in an email address, preserving the
 * caret.
 *
 * CHANGE: email fields used `removeInvalidTextCharacters`, whose allow-list
 * (`[a-zA-Z0-9\s,.@+'/]`) has no HYPHEN and no UNDERSCORE. Typing
 * `kiran@procure-wiz.com` silently produced `kiran@procurewiz.com` — the
 * character vanished as it was typed, with no error to explain why, and the
 * address then failed to match the real inbox. Hyphenated domains are
 * extremely common, so this is its own handler rather than a widening of the
 * generic text one, which is still right for names.
 */
export const removeInvalidEmailCharacters = (
  e: React.FormEvent<HTMLInputElement>
) => {
  const input = e.currentTarget;
  const sanitized = input.value.replace(INVALID_EMAIL_CHARS, "");

  if (input.value === sanitized) return;

  // Keep the caret where the user left it; replacing `value` otherwise jumps
  // it to the end and the next character lands in the wrong place.
  const caret = Math.max(0, (input.selectionStart ?? sanitized.length) - 1);
  input.value = sanitized;
  requestAnimationFrame(() => input.setSelectionRange(caret, caret));
};

/** Blocks an invalid email character at the keystroke, before it is inserted. */
export const preventInvalidEmailKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) return;

  // Only single-character keys are candidates; named keys (Shift, F5, …) pass.
  if (e.key.length !== 1) return;

  if (INVALID_EMAIL_CHARS.test(e.key)) e.preventDefault();
  // A /g regex carries lastIndex between calls, which would make every second
  // test miss. Reset it rather than relying on the next call's luck.
  INVALID_EMAIL_CHARS.lastIndex = 0;
};

/**
 * Validates if the input is a valid website URL ending with .com, .in, or .org
 */
export const isValidWebsite = (value: string): boolean => {
  if (!value) return true;

  // Matches optional http/https, optional www, domain name, and strictly .com, .in, or .org TLDs
  // Also allows optional paths at the end (e.g., .com/about)
  const websiteRegex = /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.(com|in|org)(\/.*)?$/i;

  return websiteRegex.test(value);
};

/**
 * Prevents users from typing spaces and invalid URL characters in the website field.
 */
export const preventInvalidURLCharKeyDown = (
  e: React.KeyboardEvent<HTMLInputElement>
) => {
  const allowedKeys = [
    "Backspace",
    "Delete",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Home",
    "End",
  ];

  // Allow navigation/control keys
  if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
    return;
  }

  // Allow only letters, numbers, and standard URL characters (-, ., :, /, _, ?, =, &)
  const regex = /^[a-zA-Z0-9\-.:/_?=&]$/;

  if (!regex.test(e.key)) {
    e.preventDefault();
  }
};


export const preventSpaceInputInField = (e: React.FormEvent<HTMLInputElement>) => {
  const target = e.target as HTMLInputElement;
  if (target.value.includes(" ")) {
    target.value = target.value.replace(/\s/g, "");
  }
};